import React, { useState, useEffect, useCallback } from 'react';
import { Grid, List } from 'lucide-react';
import apiService from '../../services/api';
import { exportToCsv } from '../../utils/helpers';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useAppContext } from '../../context/AppContext';

const SeasonImages = ({ programId }) => {
  const { t } = useAppContext();
  const [activeSeason, setActiveSeason] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [viewMode, setViewMode] = useState('grid');
  const [imageData, setImageData] = useState([]);
  const [imageCategories, setImageCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isZipping, setIsZipping] = useState(false);

  const getSeasonIdentifier = (season) => {
    if (!season) return null;
    return season.seasonNumber != null ? season.seasonNumber : season.seasonYear;
  };

  const getSeasonOrderValue = (season) => {
    const identifier = getSeasonIdentifier(season);
    return identifier != null ? identifier : Number.MAX_SAFE_INTEGER;
  };

  const isExpired = (expiredDate) => {
    if (!expiredDate) return false;
    const expiry = new Date(expiredDate);
    if (expiry < new Date()) {
      return `(expired: ${expiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
    }
    return false;
  };

  const fetchSeasons = useCallback(async () => {
    if (!programId) return;

    setIsLoading(true);
    setError(null);
    setSeasons([]);
    setActiveSeason(null);

    try {
      const seasonsData = await apiService.getSeasons(programId);

      if (seasonsData && seasonsData.length > 0) {
        const sortedSeasons = [...seasonsData].sort(
          (a, b) => getSeasonOrderValue(a) - getSeasonOrderValue(b)
        );
        setSeasons(sortedSeasons);
        setActiveSeason(getSeasonIdentifier(sortedSeasons[0]));
      } else {
        setSeasons([]);
      }
    } catch (err) {
      console.error('Failed to fetch seasons data:', err);
      setError(t('seasonImages.errorSeasons'));
    } finally {
      setIsLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchSeasons();
  }, [fetchSeasons]);

  // Function to fetch image categories for the selected season
  const fetchImageCategories = useCallback(async (seasonTMSId) => {
    if (!seasonTMSId) return;
    setIsCategoryLoading(true);
    setError(null);
    setImageCategories([]);
    try {
      const data = await apiService.getSeasonImages(seasonTMSId);

      // Extract unique categories from the image data
      if (data && data.length > 0) {
        const categories = [...new Set(data.map(img => img.category).filter(Boolean))];
        setImageCategories(categories);
      } else {
        setImageCategories([]);
      }
    } catch (err) {
      console.error('Failed to fetch season image categories:', err);
      setError(t('seasonImages.errorCategories'));
      setImageCategories([]);
    } finally {
      setIsCategoryLoading(false);
    }
  }, []);

  // Function to fetch images for a specific category
  const fetchImagesByCategory = useCallback(async (seasonTMSId, category) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getSeasonImages(seasonTMSId);

      // Filter images by the selected category
      const filteredImages = data.filter(img => img.category === category);
      setImageData(filteredImages || []);
    } catch (err) {
      console.error('Failed to fetch season images:', err);
      setError(t('seasonImages.errorImages'));
      setImageData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle season selection
  const handleSeasonSelect = useCallback((seasonIdentifier) => {
    setActiveSeason(seasonIdentifier);
    setActiveCategory(null);
    setImageData([]);
    setImageCategories([]);
    setSelectedImages([]);
    
    // Find the selected season and fetch its images
    const selectedSeason = seasons.find(s => getSeasonIdentifier(s) === seasonIdentifier);
    if (selectedSeason && selectedSeason.seasonTMSId) {
      fetchImageCategories(selectedSeason.seasonTMSId);
    }
  }, [seasons, fetchImageCategories]);

  // Handle category selection
  const handleCategorySelect = useCallback((category) => {
    setActiveCategory(category);
    const selectedSeason = seasons.find(s => getSeasonIdentifier(s) === activeSeason);
    if (selectedSeason && selectedSeason.seasonTMSId) {
      fetchImagesByCategory(selectedSeason.seasonTMSId, category);
    }
  }, [activeSeason, seasons, fetchImagesByCategory]);

  // Auto-fetch categories when a season is selected
  useEffect(() => {
    if (activeSeason && seasons.length > 0) {
      const selectedSeason = seasons.find(s => getSeasonIdentifier(s) === activeSeason);
      if (selectedSeason && selectedSeason.seasonTMSId) {
        fetchImageCategories(selectedSeason.seasonTMSId);
      }
    }
  }, [activeSeason, seasons, fetchImageCategories]);

  // Auto-select first category when categories are loaded
  useEffect(() => {
    if (imageCategories.length > 0 && !activeCategory) {
      handleCategorySelect(imageCategories[0]);
    }
  }, [imageCategories, activeCategory, handleCategorySelect]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleImageSelect = (image) => {
    setSelectedImages(prevSelected => {
      if (prevSelected.find(i => i.assetId === image.assetId)) {
        return prevSelected.filter(i => i.assetId !== image.assetId);
      } else {
        return [...prevSelected, image];
      }
    });
  };

  const handleDownloadZip = async () => {
    setIsZipping(true);
    const zip = new JSZip();
    const promises = selectedImages.map(async (image) => {
      const imageUrl = image.image_url.split('?')[0];
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const filename = imageUrl.split('/').pop();
      zip.file(filename, blob);
    });

    await Promise.all(promises);
    zip.generateAsync({ type: 'blob' }).then((content) => {
      saveAs(content, `season${activeSeason}-selectedimages.zip`);
      setIsZipping(false);
      setIsModalOpen(false);
    });
  };

  const handleDownloadAllZip = async () => {
    setIsZipping(true);
    try {
      const selectedSeason = seasons.find(s => getSeasonIdentifier(s) === activeSeason);
      if (!selectedSeason || !selectedSeason.seasonTMSId) return;
      
      const allImages = await apiService.getSeasonImages(selectedSeason.seasonTMSId);
      const zip = new JSZip();
      const promises = allImages.map(async (image) => {
        const imageUrl = image.image_url.split('?')[0];
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const filename = imageUrl.split('/').pop();
        zip.file(filename, blob);
      });

      await Promise.all(promises);
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, `season${activeSeason}-allimages.zip`);
        setIsZipping(false);
      });
    } catch (err) {
      console.error('Failed to fetch all images for zipping:', err);
      setError(t('seasonImages.errorDownload'));
      setIsZipping(false);
    }
  };

  if (isLoading && !seasons.length) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="flex flex-col items-center">
          <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">{t('seasonImages.loadingSeasons')}</span>
        </div>
      </div>
    );
  }

  if (error && !seasons.length) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-red-500 text-center">
          <p className="mb-4">{error}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={fetchSeasons}
          >
            {t('programTitles.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!seasons || seasons.length === 0) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('seasonImages.noSeasons')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-4 overflow-auto">
      {/* Season selection bar */}
      <div className="flex mb-6">
        <h3 className="text-gray-500 text-sm mr-4">{t('seasonImages.title')}</h3>
        <div className="flex flex-wrap gap-2">
          {seasons.map((season) => (
            <button
              key={season.seasonId}
              className={`w-10 h-10 text-sm font-medium rounded-full flex items-center justify-center ${
                activeSeason === getSeasonIdentifier(season)
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => handleSeasonSelect(getSeasonIdentifier(season))}
            >
              {season.seasonNumber != null ? season.seasonNumber : season.seasonYear}
            </button>
          ))}
        </div>
      </div>

      {/* Category selection and view options */}
      {imageCategories.length > 0 && (
        <div className="flex justify-between items-center mb-4 pb-2 border-b">
          <div className="flex flex-wrap gap-2">
            {imageCategories.map((category, index) => (
              <button
                key={index}
                onClick={() => handleCategorySelect(category)}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  activeCategory === category 
                    ? 'bg-blue-100 border-blue-300 text-blue-700' 
                    : 'bg-gray-100 border-gray-200 hover:bg-blue-50 hover:border-blue-200 text-gray-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
          
          <div className="flex items-center">
            {selectedImages.length > 0 && (
              <button
                className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm mr-2"
                onClick={() => setIsModalOpen(true)}
              >
                {isZipping ? t('programImages.zipping') : t('programImages.downloadAsZip')}
              </button>
            )}
            {imageData.length > 0 && (
              <button
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm mr-4"
                onClick={handleDownloadAllZip}
                disabled={isZipping}
              >
                {isZipping ? t('seasonImages.zippingAll') : t('seasonImages.downloadAllAsZip')}
              </button>
            )}
            <button 
              className="px-2 py-1 border border-gray-300 rounded-l flex items-center text-xs"
              onClick={() => setViewMode('grid')}
            >
              <Grid size={14} className={`mr-1 ${viewMode === 'grid' ? 'text-blue-600' : 'text-gray-600'}`} />
              <span className={viewMode === 'grid' ? 'text-blue-600' : 'text-gray-600'}>{t('seasonImages.viewGrid')}</span>
            </button>
            <button 
              className="px-2 py-1 border border-gray-300 border-l-0 rounded-r flex items-center text-xs"
              onClick={() => setViewMode('list')}
            >
              <List size={14} className={`mr-1 ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-600'}`} />
              <span className={viewMode === 'list' ? 'text-blue-600' : 'text-gray-600'}>{t('seasonImages.viewList')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Loading state for images */}
      {(isLoading || isCategoryLoading) && imageCategories.length === 0 ? (
        <div className="w-full p-8 flex justify-center items-center">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600 text-sm">{t('seasonImages.loading')}</span>
          </div>
        </div>
      ) : imageCategories.length === 0 ? (
        <div className="w-full p-8 flex justify-center items-center">
          <div className="text-gray-500 text-center">
            <p>{t('seasonImages.noImages', { season: activeSeason })}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Grid view */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {imageData.map((image, index) => {
                const expired = isExpired(image.expiredDate);
                return (
                  <div
                    key={image.assetId || index}
                    className={`relative border rounded-md overflow-hidden transition-colors ${selectedImages.find(i => i.assetId === image.assetId) ? 'border-blue-500' : 'border-gray-100'} ${expired ? 'bg-yellow-100' : 'bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      className="absolute top-2 left-2 z-10 h-5 w-5"
                      checked={selectedImages.some(i => i.assetId === image.assetId)}
                      onChange={() => handleImageSelect(image)}
                    />
                    <div 
                      className="aspect-video bg-gray-200 flex items-center justify-center cursor-pointer"
                      onClick={() => handleImageClick(image)}
                    >
                      {image.image_url ? (
                        <img
                          src={image.image_url.replace('w=240', 'w=400')}
                          alt={image.caption || `${image.category} image`}
                          className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://via.placeholder.com/400x225?text=${t('programImages.noImage').replace(' ', '+')}`;
                          }}
                        />
                      ) : (
                        <div className="text-3xl font-bold text-blue-300">
                          {image.category ? image.category[0] : 'I'}
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className="text-xs font-medium">{image.category || 'Unknown'} {expired && <span className="text-red-500">{expired}</span>}</div>
                      <div className="text-xs text-gray-500">{image.assetId || 'Unknown'}</div>
                      {image.width && image.height && (
                        <div className="text-xs text-gray-500">
                          {image.width} x {image.height} ({image.ratio || 'Unknown'})
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.select')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.preview')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.category')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.assetId')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.resolution')}</th>
                  <th className="px-4 py-2 font-medium text-gray-600">{t('programImages.columns.ratio')}</th>
                </tr>
              </thead>
              <tbody>
                {imageData.map((image, index) => {
                  const expired = isExpired(image.expiredDate);
                  return (
                    <tr
                      key={image.assetId || index}
                      className={`border-t border-gray-200 hover:bg-blue-50 cursor-pointer ${selectedImages.find(i => i.assetId === image.assetId) ? 'bg-blue-100' : ''} ${expired ? 'bg-yellow-100' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          className="h-5 w-5"
                          checked={selectedImages.some(i => i.assetId === image.assetId)}
                          onChange={() => handleImageSelect(image)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </td>
                      <td className="px-4 py-3" onClick={() => handleImageClick(image)}>
                        <div className="w-16 h-9 bg-gray-200 flex items-center justify-center overflow-hidden">
                          {image.image_url ? (
                            <img
                              src={image.image_url}
                              alt={image.caption || `${image.category} image`}
                              className="max-w-full max-h-full object-contain"
                            />
                          ) : (
                            <span className="text-blue-300">{t('programImages.noImage')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-blue-600">{image.category || 'Unknown'} {expired && <span className="text-red-500">{expired}</span>}</td>
                      <td className="px-4 py-3">{image.assetId || 'Unknown'}</td>
                      <td className="px-4 py-3">{image.width && image.height ? `${image.width} x ${image.height}` : 'Unknown'}</td>
                      <td className="px-4 py-3">{image.ratio || 'Unknown'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Status bar */}
          {imageData.length > 0 && (
            <div className="mt-4 flex justify-between text-xs text-gray-500">
              <div>
                {t('seasonImages.showing', { count: imageData.length, category: activeCategory, season: activeSeason })}
              </div>
              <div>
                <button
                  className={`px-3 py-1 border border-gray-300 rounded-sm ${imageData.length === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
                  onClick={() => exportToCsv(imageData, `season${activeSeason}_${activeCategory}_images.csv`)}
                  disabled={imageData.length === 0}
                >
                  {t('seasonImages.downloadCategoryImages', { category: activeCategory })}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Image Popup Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
          onClick={() => setSelectedImage(null)}
          style={{ zIndex: 9999 }}
        >
          <div className="relative bg-white rounded-lg overflow-hidden max-w-4xl max-h-screen" onClick={e => e.stopPropagation()}>
            <button 
              className="absolute top-2 right-2 bg-white rounded-full p-1 text-gray-800 hover:text-red-500 z-10"
              onClick={() => setSelectedImage(null)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-auto p-1 bg-gray-100 flex items-center justify-center">
                <img 
                  src={selectedImage.image_url ? selectedImage.image_url.split('?')[0] : ''}
                  alt={selectedImage.caption || `${selectedImage.category} image`}
                  className="max-w-full max-h-[70vh] object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                  }}
                />
              </div>
              
              <div className="p-4 border-t border-gray-200">
                <div className="font-medium">{selectedImage.category || 'Unknown'}</div>
                <div className="text-sm text-gray-600">{t('programImages.columns.assetId')}: {selectedImage.assetId || 'Unknown'}</div>
                {selectedImage.width && selectedImage.height && (
                  <div className="text-sm text-gray-600">
                    {t('programImages.columns.resolution')}: {selectedImage.width} x {selectedImage.height} ({selectedImage.ratio || 'Unknown'})
                  </div>
                )}
                {selectedImage.caption && (
                  <div className="text-sm text-gray-600 mt-2">
                    <span className="font-medium">{t('programImages.caption')}</span> {selectedImage.caption}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download Zip Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" 
          onClick={() => setIsModalOpen(false)}
          style={{ zIndex: 9999 }}
        >
          <div className="relative bg-white rounded-lg overflow-hidden max-w-4xl w-full" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="text-lg font-medium">{t('programImages.modal.title')}</h2>
            </div>
            <div className="p-4 max-h-[70vh] overflow-auto">
              <p>{t('programImages.modal.text', { count: selectedImages.length })}</p>
              <ul className="list-disc list-inside mt-4">
                {selectedImages.map(image => (
                  <li key={image.assetId} className="text-sm">
                    {image.image_url.split('?')[0].split('/').pop()}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                onClick={() => setIsModalOpen(false)}
              >
                {t('programImages.modal.cancel')}
              </button>
              <button
                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={handleDownloadZip}
                disabled={isZipping}
              >
                {isZipping ? t('programImages.zipping') : t('programImages.modal.download')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SeasonImages;
